<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useNotificationsStore } from '@/stores/notifications'
import { getInitials } from '@/utils/formatters'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import ThemeToggle from './ThemeToggle.vue'
import NotificationsDrawer from './NotificationsDrawer.vue'
import MeetingsWidget from './MeetingsWidget.vue'
import GlobalSearch from './GlobalSearch.vue'
import { useTasksWidget } from '@/composables/useTasksWidget'
import { Bell, User, Settings, LogOut, Globe, CalendarDays, X, CheckSquare, Building2, Check } from 'lucide-vue-next'
import type { Tenant } from '@/types'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const {
  user, logout, updatePreferences, isImpersonating, currentTenant, stopImpersonation,
  canSwitchTenant, fetchSelectableTenants, selectTenant, effectiveTenantId, isSuperAdmin,
} = useAuth()
const notificationsStore = useNotificationsStore()
const tasksWidget = useTasksWidget()

const isNotificationsOpen = ref(false)
const isMeetingsOpen = ref(false)

// Multi-tenant (Modelo C): tenants entre los que el usuario puede cambiar.
const selectableTenants = ref<Tenant[]>([])
const loadSelectableTenants = async () => {
  selectableTenants.value = canSwitchTenant.value ? await fetchSelectableTenants() : []
}
onMounted(loadSelectableTenants)
watch(() => user.value?.id, loadSelectableTenants)

const handleSelectTenant = async (tenantId: string) => {
  if (tenantId !== effectiveTenantId.value) await selectTenant(tenantId)
}

const breadcrumbs = computed(() => {
  const crumbs: Array<{ title: string; path?: string }> = [
    { title: t('navigation.dashboard'), path: '/dashboard' },
  ]

  const pathParts = route.path.split('/').filter(Boolean)

  if (pathParts.length > 1) {
    const section = pathParts[0]
    const sectionTitle = t(`navigation.${section}`, section)
    crumbs.push({ title: sectionTitle, path: `/${section}` })
  }

  if (route.meta?.title) {
    crumbs.push({ title: route.meta.title as string })
  }

  return crumbs
})

const toggleLanguage = async () => {
  const newLocale = locale.value === 'es' ? 'en' : 'es'
  locale.value = newLocale
  localStorage.setItem('locale', newLocale)

  if (user.value) {
    try {
      await updatePreferences({ language: newLocale })
    } catch {
      // Silently fail
    }
  }
}

const handleLogout = async () => {
  await logout()
}

const goToProfile = () => {
  router.push('/profile')
}

const goToSettings = () => {
  router.push('/settings')
}

const openNotifications = () => {
  isNotificationsOpen.value = true
}

const openMeetings = () => {
  isMeetingsOpen.value = true
}

const handleStopImpersonation = async () => {
  if (isSuperAdmin.value) {
    // Superadmin: salir de la impersonación y volver al panel de tenants.
    await stopImpersonation()
    router.push('/super-admin/tenants')
  } else {
    // Multi-tenant: volver al tenant PRIMARIO (selectTenant persiste + recarga).
    if (user.value?.tenantId) await selectTenant(user.value.tenantId)
  }
}
</script>

<template>
  <!-- Tenant activo / Impersonación (solo con el nombre resuelto, para evitar flashes) -->
  <div
    v-if="isImpersonating && currentTenant?.name"
    class="flex items-center justify-between gap-2 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950"
  >
    <span v-if="isSuperAdmin">Impersonando tenant: <strong>{{ currentTenant.name }}</strong></span>
    <span v-else>Trabajando en: <strong>{{ currentTenant.name }}</strong></span>
    <Button variant="ghost" size="sm" class="h-6 text-amber-950 hover:bg-amber-600 hover:text-amber-950" @click="handleStopImpersonation">
      <X class="mr-1 h-3 w-3" />
      {{ isSuperAdmin ? 'Salir' : 'Volver a mi tenant' }}
    </Button>
  </div>

  <header class="flex h-16 shrink-0 items-center gap-2 border-b px-4">
    <!-- Spacer for mobile hamburger button -->
    <div class="w-9 md:hidden" />

    <Breadcrumb class="hidden md:flex">
      <BreadcrumbList>
        <template v-for="(crumb, index) in breadcrumbs" :key="index">
          <BreadcrumbItem>
            <BreadcrumbLink v-if="crumb.path" :href="crumb.path">
              {{ crumb.title }}
            </BreadcrumbLink>
            <BreadcrumbPage v-else>{{ crumb.title }}</BreadcrumbPage>
          </BreadcrumbItem>
          <BreadcrumbSeparator v-if="index < breadcrumbs.length - 1" />
        </template>
      </BreadcrumbList>
    </Breadcrumb>

    <div class="ml-auto flex items-center gap-2">
      <!-- Global Search -->
      <GlobalSearch />

      <!-- Tenant Selector (multi-tenant, Modelo C) -->
      <DropdownMenu v-if="canSwitchTenant && selectableTenants.length">
        <DropdownMenuTrigger as-child>
          <Button variant="outline" size="sm" class="gap-2">
            <Building2 class="h-4 w-4" />
            <span class="hidden md:inline max-w-[10rem] truncate">{{ currentTenant?.name || 'Tenant' }}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-56">
          <DropdownMenuLabel>Cambiar de tenant</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            v-for="tenant in selectableTenants"
            :key="tenant.id"
            class="flex items-center justify-between gap-2"
            @click="handleSelectTenant(tenant.id)"
          >
            <span class="truncate">{{ tenant.name }}</span>
            <Check v-if="tenant.id === effectiveTenantId" class="h-4 w-4 shrink-0" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- Language Toggle -->
      <Button variant="ghost" size="icon" @click="toggleLanguage">
        <Globe class="h-5 w-5" />
        <span class="sr-only">{{ t('settings.language.title') }}</span>
      </Button>

      <!-- Theme Toggle -->
      <ThemeToggle />

      <!-- Tasks Widget -->
      <Button variant="ghost" size="icon" @click="tasksWidget.toggle()">
        <CheckSquare class="h-5 w-5" />
      </Button>

      <!-- Meetings / Calendar -->
      <Button variant="ghost" size="icon" @click="openMeetings">
        <CalendarDays class="h-5 w-5" />
      </Button>

      <MeetingsWidget v-model:open="isMeetingsOpen" />

      <!-- Notifications -->
      <Button variant="ghost" size="icon" class="relative" @click="openNotifications">
        <Bell class="h-5 w-5" />
        <Badge
          v-if="notificationsStore.hasUnread"
          variant="destructive"
          class="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
        >
          {{ notificationsStore.unreadCount > 9 ? '9+' : notificationsStore.unreadCount }}
        </Badge>
        <span class="sr-only">{{ t('navigation.notifications') }}</span>
      </Button>

      <!-- Notifications Drawer -->
      <NotificationsDrawer v-model:open="isNotificationsOpen" />

      <!-- User Menu -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" class="relative h-9 w-9 rounded-full">
            <Avatar class="h-9 w-9">
              <AvatarImage v-if="user?.avatarUrl" :src="user.avatarUrl" :alt="user.displayName" />
              <AvatarFallback>{{ getInitials(user?.displayName) }}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56" align="end">
          <DropdownMenuLabel class="font-normal">
            <div class="flex flex-col space-y-1">
              <p class="text-sm font-medium leading-none">{{ user?.displayName }}</p>
              <p class="text-xs leading-none text-muted-foreground">{{ user?.email }}</p>
              <p v-if="currentTenant?.name" class="text-xs leading-none text-muted-foreground">{{ currentTenant.name }}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="goToProfile">
            <User class="mr-2 h-4 w-4" />
            {{ t('navigation.profile') }}
          </DropdownMenuItem>
          <DropdownMenuItem @click="goToSettings">
            <Settings class="mr-2 h-4 w-4" />
            {{ t('navigation.settings') }}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="handleLogout" class="text-destructive">
            <LogOut class="mr-2 h-4 w-4" />
            {{ t('auth.logout') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>
